import React from 'react';
import { BP_PRIMARY_BUTTON_SMALL_CLASSES, BP_TOOLBAR_BUTTON_SMALL_CLASSES } from '../../../Components/brandPresenceClasses';
import { SectionLoadFailed } from '../../../Components/SectionLoadFailed';
import { FilterDropdownList, IFilterDropdownOption, IFilterDropdownOptionGroup } from './FilterDropdownList';

export interface IMultiLevelDropdownTarget {
  id: string;
  label: string;
}

export function MultiLevelDropdownPanel({
  selectedTarget,
  targetSearch,
  onTargetSearchChange,
  targetSearchPlaceholder,
  selectedTargetOptions,
  filteredTargetOptions,
  groupedFilteredTargetOptions,
  loadingTargets,
  onSelectTarget,
  valueSearch,
  onValueSearchChange,
  valueSearchPlaceholder,
  selectedValueOptions,
  filteredValueOptions,
  loadingValues,
  valueSelectionMultiple,
  onToggleValue,
  hideSelectedValuesFromFiltered,
  onLoadMoreValues,
  canLoadMoreValues,
  changeTargetLabel,
  onBackToTargets,
  cancelLabel,
  applyLabel,
  onCancel,
  onApply,
  disableApply,
  emptyValuesMessage,
  valuesLoadFailed,
}: {
  selectedTarget?: IMultiLevelDropdownTarget;
  targetSearch: string;
  onTargetSearchChange: (value: string) => void;
  targetSearchPlaceholder: string;
  selectedTargetOptions: IFilterDropdownOption[];
  filteredTargetOptions: IFilterDropdownOption[];
  groupedFilteredTargetOptions?: IFilterDropdownOptionGroup[];
  loadingTargets?: boolean;
  onSelectTarget: (option: IFilterDropdownOption) => void;
  valueSearch: string;
  onValueSearchChange: (value: string) => void;
  valueSearchPlaceholder: string;
  selectedValueOptions: IFilterDropdownOption[];
  filteredValueOptions: IFilterDropdownOption[];
  loadingValues?: boolean;
  valueSelectionMultiple: boolean;
  onToggleValue: (option: IFilterDropdownOption) => void;
  hideSelectedValuesFromFiltered?: boolean;
  onLoadMoreValues?: () => void;
  canLoadMoreValues?: boolean;
  changeTargetLabel?: string;
  onBackToTargets: () => void;
  cancelLabel?: string;
  applyLabel?: string;
  onCancel: () => void;
  onApply: () => void;
  disableApply?: boolean;
  emptyValuesMessage?: string;
  /**
   * Given when reading the values failed: what went wrong, said in place of `emptyValuesMessage`, and
   * the callback that reads them again. A failed read never reads as one that found nothing.
   */
  valuesLoadFailed?: { message: string; onRetry: () => void };
}) {
  if (!selectedTarget) {
    return (
      <FilterDropdownList
        search={targetSearch}
        onSearchChange={onTargetSearchChange}
        searchPlaceholder={targetSearchPlaceholder}
        selectedOptions={selectedTargetOptions}
        filteredOptions={filteredTargetOptions}
        groupedFilteredOptions={groupedFilteredTargetOptions}
        multiple={false}
        loading={loadingTargets}
        onToggle={onSelectTarget}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/[0.07]"
          onClick={onBackToTargets}
        >
          {changeTargetLabel || 'Change filter'}
        </button>
        <span className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">{selectedTarget.label}</span>
      </div>

      <FilterDropdownList
        search={valueSearch}
        onSearchChange={onValueSearchChange}
        searchPlaceholder={valueSearchPlaceholder}
        selectedOptions={selectedValueOptions}
        filteredOptions={filteredValueOptions}
        multiple={valueSelectionMultiple}
        loading={loadingValues}
        onToggle={onToggleValue}
        hideSelectedFromFiltered={hideSelectedValuesFromFiltered}
        onLoadMore={onLoadMoreValues}
        canLoadMore={canLoadMoreValues}
      />

      {valuesLoadFailed ? (
        <div className="mt-2">
          <SectionLoadFailed message={valuesLoadFailed.message} onRetry={valuesLoadFailed.onRetry} />
        </div>
      ) : !loadingValues && filteredValueOptions.length === 0 && emptyValuesMessage ? (
        <p className="mt-2 text-xs text-slate-500">{emptyValuesMessage}</p>
      ) : null}

      <div className="mt-3 flex items-center justify-end gap-2">
        <button type="button" className={BP_TOOLBAR_BUTTON_SMALL_CLASSES} onClick={onCancel}>
          {cancelLabel || 'Cancel'}
        </button>
        <button type="button" className={BP_PRIMARY_BUTTON_SMALL_CLASSES} onClick={onApply} disabled={disableApply}>
          {applyLabel || 'Apply'}
        </button>
      </div>
    </div>
  );
}
